import { useRef, useEffect } from "react";
import { Bold, Italic, Link2, Heading2, Heading3, List, ListOrdered, Quote, Undo2, Redo2 } from "lucide-react";

/**
 * Lightweight contenteditable rich-text editor for the admin CMS.
 * Emits HTML on change. Uses execCommand — good enough for editorial blog posts.
 */
export default function RichEditor({ value, onChange, placeholder = "Write your article…" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange?.(ref.current?.innerHTML || "");
  };
  const insertLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (url) exec("createLink", url);
  };
  const wrapHeading = (tag) => {
    exec("formatBlock", tag);
  };

  const btn = "p-2 rounded hover:bg-[#5E3A5C]/40 text-[#F3E9EC]/75 hover:text-white transition-colors";

  return (
    <div className="rounded-xl border border-[#5E3A5C]/40 bg-[#0B0E1A]/50 overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-[#5E3A5C]/40 bg-[#0B0E1A]/70">
        <button type="button" onClick={() => wrapHeading("H2")} className={btn} title="Heading 2" data-testid="rt-h2"><Heading2 className="w-4 h-4"/></button>
        <button type="button" onClick={() => wrapHeading("H3")} className={btn} title="Heading 3" data-testid="rt-h3"><Heading3 className="w-4 h-4"/></button>
        <button type="button" onClick={() => wrapHeading("P")} className={btn + " text-xs font-mono px-2"} title="Paragraph" data-testid="rt-p">P</button>
        <span className="w-px h-5 bg-[#5E3A5C]/40 mx-1"/>
        <button type="button" onClick={() => exec("bold")} className={btn} title="Bold" data-testid="rt-bold"><Bold className="w-4 h-4"/></button>
        <button type="button" onClick={() => exec("italic")} className={btn} title="Italic" data-testid="rt-italic"><Italic className="w-4 h-4"/></button>
        <button type="button" onClick={insertLink} className={btn} title="Insert link" data-testid="rt-link"><Link2 className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#5E3A5C]/40 mx-1"/>
        <button type="button" onClick={() => exec("insertUnorderedList")} className={btn} title="Bulleted list" data-testid="rt-ul"><List className="w-4 h-4"/></button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={btn} title="Numbered list" data-testid="rt-ol"><ListOrdered className="w-4 h-4"/></button>
        <button type="button" onClick={() => wrapHeading("BLOCKQUOTE")} className={btn} title="Quote" data-testid="rt-quote"><Quote className="w-4 h-4"/></button>
        <span className="w-px h-5 bg-[#5E3A5C]/40 mx-1"/>
        <button type="button" onClick={() => exec("undo")} className={btn} title="Undo" data-testid="rt-undo"><Undo2 className="w-4 h-4"/></button>
        <button type="button" onClick={() => exec("redo")} className={btn} title="Redo" data-testid="rt-redo"><Redo2 className="w-4 h-4"/></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        onBlur={(e) => onChange?.(e.currentTarget.innerHTML)}
        className="rt-content min-h-[260px] px-5 py-4 text-[#F3E9EC]/90 outline-none focus:bg-[#0B0E1A]/70"
        data-placeholder={placeholder}
        data-testid="rt-editor"
      />
    </div>
  );
}
